// Saved macros pinned about the page: drag a macro's button out of the Saved macros tab onto the header, the
// View (where it is let go) or a panel (a bar along its bottom) and a button that runs it stays there. Drag a
// pin to move it, off every place it can go (or back onto the Saved macros tab) to drop it; right-click drops it
// too. Kept in this browser, by macro name: a pin whose macro is gone (renamed, deleted) shows struck through.
// On the View, a button let go on another's left or right edge joins it in a row (a group, moved as one by a
// handle on its left); on its top or bottom edge, in a column (a stack): a block of buttons is a grid of both.

import type { Macro } from './macros.ts';

/** A button in a block on the View: its row and column in the block's grid. */
export interface Cell { name: string; r: number; c: number }
/** Buttons on the View: x, y the block's top-left, fractions of the View's width and height; one button, or a grid. */
export interface ViewBlock { x: number; y: number; cells: Cell[] }
export interface Pins { top: string[]; view: ViewBlock[]; panes: Record<string, string[]> }
export type Side = 'left' | 'right' | 'top' | 'bottom';
/**
 * Where a pin goes (or is): the header, the View (on its own at x, y), the bar along the bottom of the panel of
 * data-tab tab, or into View block b at a side of its button at row r, column c.
 */
export type Zone = { k: 'top' } | { k: 'view'; x: number; y: number } | { k: 'pane'; tab: string }
  | { k: 'join'; b: number; r: number; c: number; side: Side };
/** A pin where it is: its zone, and its place in that zone's list (on the View: cell i of block b). */
export interface At { zone: Zone; i: number; b?: number }

/** Panels with no bar: the View (pins go on it, where let go), the macros themselves, and those a bar would cover. */
export const NO_BAR = new Set(['viewport', 'macros', 'nodes', 'construction']);

const KEY = 'cloudview.macropins.v1';
const clamp01 = (v: number) => Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));
const str = (v: unknown): v is string => typeof v === 'string' && v.length > 0;
const uniq = (a: string[]) => [...new Set(a)];
const idx = (v: unknown) => Number.isInteger(v) && (v as number) >= 0 && (v as number) < 1000;
/** Marks a moved View button where it was, until it is put in its new place (so no index shifts under it). */
const MOVED = '';

export const emptyPins = (): Pins => ({ top: [], view: [], panes: {} });

/** Cells renumbered so no row or column is empty, in reading order. */
export function compact(cells: Cell[]): Cell[] {
  const rows = [...new Set(cells.map(c => c.r))].sort((a, b) => a - b), cols = [...new Set(cells.map(c => c.c))].sort((a, b) => a - b);
  return cells.map(c => ({ name: c.name, r: rows.indexOf(c.r), c: cols.indexOf(c.c) })).sort((a, b) => a.r - b.r || a.c - b.c);
}

/** Pins from storage: anything that doesn't fit is left out. */
export function parsePins(s: unknown): Pins {
  const p = emptyPins();
  if (!s || typeof s !== 'object') return p;
  const o = s as Record<string, unknown>;
  if (Array.isArray(o.top)) p.top = uniq(o.top.filter(str));
  if (Array.isArray(o.view)) for (const v of o.view) {
    if (!v || typeof v !== 'object') continue;
    const b = v as Record<string, unknown>, x = clamp01(+(b.x as number)), y = clamp01(+(b.y as number));
    if (str(b.name)) { p.view.push({ x, y, cells: [{ name: b.name, r: 0, c: 0 }] }); continue; }   // a lone pin, as kept before blocks
    if (!Array.isArray(b.cells)) continue;
    const seen = new Set<string>(), cells: Cell[] = [];
    for (const c of b.cells as Cell[]) {
      if (!c || !str(c.name) || !idx(c.r) || !idx(c.c) || seen.has(`${c.r},${c.c}`)) continue;
      seen.add(`${c.r},${c.c}`);
      cells.push({ name: c.name, r: c.r, c: c.c });
    }
    if (cells.length) p.view.push({ x, y, cells: compact(cells) });
  }
  if (o.panes && typeof o.panes === 'object') for (const [k, v] of Object.entries(o.panes)) {
    if (!NO_BAR.has(k) && Array.isArray(v)) { const a = uniq(v.filter(str)); if (a.length) p.panes[k] = a; }
  }
  return p;
}

const copy = (p: Pins): Pins => ({ top: [...p.top], view: p.view.map(v => ({ ...v, cells: v.cells.map(c => ({ ...c })) })), panes: { ...p.panes } });

/** The View with moved buttons' marks taken out, each block compacted, emptied blocks gone. */
const tidy = (p: Pins): Pins => {
  for (const v of p.view) v.cells = compact(v.cells.filter(c => c.name !== MOVED));
  p.view = p.view.filter(v => v.cells.length);
  return p;
};

/**
 * Cells (a block's shape, from its top-left) put into a block's cells at a side of its cell at r, c: in the free
 * places there, else in new columns there (from a left or right) or new rows (from a top or bottom), so the
 * block's rows and columns stay lined up.
 */
export function joinCells(into: Cell[], add: Cell[], r: number, c: number, side: Side): Cell[] {
  const a = compact(add), h = Math.max(...a.map(x => x.r)) + 1, w = Math.max(...a.map(x => x.c)) + 1;
  const ar = side === 'bottom' ? r + 1 : r, ac = side === 'right' ? c + 1 : c;
  const out = into.map(x => ({ ...x }));
  const taken = new Set(out.map(x => `${x.r},${x.c}`));
  if (a.some(x => taken.has(`${ar + x.r},${ac + x.c}`))) for (const x of out) {
    if (side === 'left' || side === 'right') { if (x.c >= ac) x.c += w; }
    else if (x.r >= ar) x.r += h;
  }
  return [...out, ...a.map(x => ({ name: x.name, r: ar + x.r, c: ac + x.c }))];
}

/** The pins with the one at `from` taken out. */
export function removePin(p: Pins, from: At): Pins {
  const z = from.zone, q = copy(p);
  if (z.k === 'top') q.top.splice(from.i, 1);
  else if (z.k === 'view' || z.k === 'join') {
    const v = q.view[from.b ?? -1];
    if (v) v.cells.splice(from.i, 1);
    tidy(q);
  } else {
    const a = (q.panes[z.tab] ?? []).filter((_, i) => i !== from.i);
    if (a.length) q.panes[z.tab] = a; else delete q.panes[z.tab];
  }
  return q;
}

/** The View's block b taken out (a group dropped as one). */
export const removeBlock = (p: Pins, b: number): Pins => ({ ...copy(p), view: p.view.filter((_, i) => i !== b) });

const onView = (at?: At) => !!at && at.b !== undefined && (at.zone.k === 'view' || at.zone.k === 'join');

/**
 * Macro name pinned in zone (before the pin at index `before` of its list, else last); from: the pin being moved,
 * taken out first. A bar holds a macro once: pinned again there, it moves to its new place.
 */
export function placePin(p: Pins, name: string, zone: Zone, before?: number, from?: At): Pins {
  if (from && onView(from)) {
    // marked where it was rather than taken out, so block b of a join (it may be the one it was in) stays block b
    const q = copy(p), v = q.view[from.b!];
    if (v?.cells[from.i]) v.cells[from.i].name = MOVED;
    return tidy(placePin(q, name, zone, before));
  }
  if (from) {
    // taking the moved pin out shifts the places after it in the same bar
    if (before !== undefined && same(from.zone, zone) && zone.k !== 'view' && zone.k !== 'join' && from.i < before) before--;
    p = removePin(p, from);
  }
  const q = copy(p);
  if (zone.k === 'view') { q.view.push({ x: clamp01(zone.x), y: clamp01(zone.y), cells: [{ name, r: 0, c: 0 }] }); return q; }
  if (zone.k === 'join') {
    const v = q.view[zone.b];
    if (!v) return p;
    v.cells = joinCells(v.cells, [{ name, r: 0, c: 0 }], zone.r, zone.c, zone.side);
    return tidy(q);
  }
  if (zone.k === 'pane' && NO_BAR.has(zone.tab)) return p;
  const list = zone.k === 'top' ? q.top : [...(q.panes[zone.tab] ?? [])];
  const was = list.indexOf(name);
  if (was >= 0) { list.splice(was, 1); if (before !== undefined && was < before) before--; }
  list.splice(before === undefined ? list.length : Math.max(0, Math.min(before, list.length)), 0, name);
  if (zone.k === 'pane') q.panes[zone.tab] = list; else q.top = list;
  return q;
}

/** The View's block b moved to x, y. */
export function moveBlock(p: Pins, b: number, x: number, y: number): Pins {
  const q = copy(p);
  if (q.view[b]) Object.assign(q.view[b], { x: clamp01(x), y: clamp01(y) });
  return q;
}

/** The View's block b put into another (into: a join), keeping its shape; let go on itself, nothing changes. */
export function joinBlock(p: Pins, b: number, into: Zone): Pins {
  if (into.k !== 'join' || into.b === b || !p.view[b] || !p.view[into.b]) return p;
  const q = copy(p), t = q.view[into.b];
  t.cells = joinCells(t.cells, q.view[b].cells, into.r, into.c, into.side);
  q.view[b].cells = [];
  return tidy(q);
}

/** Where a button was grabbed: the pointer's offset into it, and its size. */
interface Grab { dx: number; dy: number; w: number; h: number }
/** Where a drag would go: a zone (before a bar's pin), or 'stay' (a View button let go on itself: nothing changes). */
type Aim = { zone: Zone; before?: number } | 'stay' | null;
/**
 * What is being dragged, as far as aiming goes: a View block (whole: i unset) or a button in it (at: where it was), and
 * if only the View takes it.
 */
interface Self { b?: number; i?: number; at?: DOMRect; viewOnly?: boolean }

const same = (a: Zone, b: Zone) => a.k === b.k && (a.k !== 'pane' || a.tab === (b as { tab: string }).tab);
/** The grip on a group's left, outside its buttons (the block is kept this far from the View's left). */
const GRIP = 10;

export interface PinHost {
  macros: () => Macro[];
  /** Run a saved macro (as its button in the Saved macros tab does). */
  run: (m: Macro) => void;
  /** A macro's button's tooltip. */
  title: (m: Macro) => string;
  /** The header, the View, and the holder the dock parks panels in (a panel is found by its data-tab wherever it is). */
  top: HTMLElement;
  view: HTMLElement;
  before?: HTMLElement | null;
}

const div = (cls: string, text?: string) => {
  const d = document.createElement('div');
  d.className = cls;
  if (text) d.textContent = text;
  return d;
};

export class MacroPins {
  private pins: Pins;
  private topBar: HTMLElement;
  /** Swallow the click a drag ends in. */
  private dragged = false;
  /** Each panel's bar's height to its panel (--pinh), for the bar to sit at the panel's bottom. */
  private barSize = new ResizeObserver(es => { for (const e of es) (e.target.parentElement as HTMLElement | null)?.style.setProperty('--pinh', `${(e.target as HTMLElement).offsetHeight}px`); });

  private h: PinHost;

  constructor(h: PinHost) {
    this.h = h;
    this.pins = this.load();
    this.topBar = document.createElement('span');
    this.topBar.id = 'macpins-top';
    h.top.insertBefore(this.topBar, h.before && h.before.parentElement === h.top ? h.before : null);
    // the View's pins keep to it as it changes size
    new ResizeObserver(() => this.placeView()).observe(h.view);
  }

  private load(): Pins {
    try { return parsePins(JSON.parse(localStorage.getItem(KEY) || 'null')); } catch { return emptyPins(); }
  }

  private save() {
    try { localStorage.setItem(KEY, JSON.stringify(this.pins)); } catch { /* storage blocked */ }
  }

  private set(p: Pins) {
    this.pins = p;
    this.save();
    this.draw();
  }

  /** Every pin drawn again (after the saved macros change). */
  draw() {
    this.topBar.replaceChildren(...this.pins.top.map((n, i) => this.pinEl(n, { zone: { k: 'top' }, i })));
    for (const b of this.h.view.querySelectorAll(':scope > .macblock')) b.remove();
    this.pins.view.forEach((v, b) => {
      const el = div('macblock onview');
      el.dataset.b = String(b);
      if (v.cells.length > 1) {
        // a group's handle: drags the whole block
        const grip = div('macblock-grip');
        grip.title = 'drag to move the group (onto another button\'s edge to join it); right-click drops the group';
        grip.onpointerdown = e => this.startBlockDrag(e, b);
        grip.oncontextmenu = e => { e.preventDefault(); this.set(removeBlock(this.pins, b)); };
        el.append(grip);
      }
      v.cells.forEach((c, i) => {
        const btn = this.pinEl(c.name, { zone: { k: 'view', x: v.x, y: v.y }, i, b });
        btn.style.gridArea = `${c.r + 1} / ${c.c + 1}`;
        btn.dataset.r = String(c.r);
        btn.dataset.c = String(c.c);
        el.append(btn);
      });
      this.h.view.append(el);
    });
    this.placeView();
    for (const b of document.querySelectorAll('.macpinbar')) {
      this.barSize.unobserve(b);
      (b.parentElement as HTMLElement).style.removeProperty('--pinh');
      b.remove();
    }
    for (const [tab, names] of Object.entries(this.pins.panes)) {
      const pane = document.querySelector<HTMLElement>(`.pane[data-tab="${CSS.escape(tab)}"]`);
      if (!pane) continue;
      const bar = div('macpinbar');
      bar.append(...names.map((n, i) => this.pinEl(n, { zone: { k: 'pane', tab }, i })));
      pane.append(bar);
      this.barSize.observe(bar);
    }
  }

  /** The View's blocks where they were let go, kept wholly in it (a group's grip too). */
  private placeView() {
    const w = this.h.view.clientWidth, h = this.h.view.clientHeight;
    for (const b of this.h.view.querySelectorAll<HTMLElement>(':scope > .macblock')) {
      const v = this.pins.view[+b.dataset.b!];
      if (!v) continue;
      const g = v.cells.length > 1 ? GRIP : 0;
      b.style.left = `${Math.max(g, Math.min(v.x * w, w - b.offsetWidth))}px`;
      b.style.top = `${Math.max(0, Math.min(v.y * h, h - b.offsetHeight))}px`;
    }
  }

  private pinEl(name: string, at: At): HTMLButtonElement {
    const m = this.h.macros().find(x => x.name === name);
    const b = document.createElement('button');
    b.className = 'macpin' + (m?.graph ? ' graph' : '') + (m ? '' : ' gone');
    b.textContent = name;
    b.dataset.i = String(at.i);
    const how = at.b === undefined ? '' : '; onto another View button\'s side to group, top or bottom to stack';
    b.title = m ? `${this.h.title(m)}\n(drag to move it${how}, off to drop it; right-click drops it)` : `${name}: no longer saved (right-click, or drag it off, to drop the pin)`;
    b.onclick = () => {
      if (this.dragged) return;
      const now = this.h.macros().find(x => x.name === name);   // as saved now
      if (now) this.h.run(now);
    };
    b.oncontextmenu = e => { e.preventDefault(); this.set(removePin(this.pins, at)); };
    b.onpointerdown = e => this.startDrag(e, name, at);
    return b;
  }

  /**
   * Pointer down on a macro's button (from: on a pin, where it is): past a few px it drags, to pin (or move) it where
   * let go. The button is held where it was grabbed: it moves with the pointer, not to it, and lands on the View there.
   */
  startDrag(e: PointerEvent, name: string, from?: At) {
    this.drag(e, e.currentTarget as HTMLElement | null, name, from?.b === undefined ? {} : { b: from.b, i: from.i }, a => {
      if (a === 'stay') return null;
      if (a) return placePin(this.pins, name, a.zone, a.before, from);
      return from ? removePin(this.pins, from) : null;   // a pin let go nowhere it can go: dropped
    });
  }

  /** Pointer down on a group's grip: drags the whole block, onto the View (moved) or another's button (joined). */
  private startBlockDrag(e: PointerEvent, b: number) {
    const block = (e.currentTarget as HTMLElement).parentElement;
    this.drag(e, block, '', { b, viewOnly: true }, a => {
      if (!a || a === 'stay') return null;   // let go off the View: back where it was (a group is dropped by right-click)
      if (a.zone.k === 'view') return moveBlock(this.pins, b, a.zone.x, a.zone.y);
      return joinBlock(this.pins, b, a.zone);
    });
    e.stopPropagation();
  }

  /** A drag of src (a copy of it held where grabbed); let go, done gives the pins after (null: no change). */
  private drag(e: PointerEvent, src: HTMLElement | null, name: string, self: Self, done: (a: Aim) => Pins | null) {
    if (e.button !== 0) return;
    const x0 = e.clientX, y0 = e.clientY;
    const r0 = src?.getBoundingClientRect();
    const grab: Grab = r0 && r0.width ? { dx: x0 - r0.left, dy: y0 - r0.top, w: r0.width, h: r0.height } : { dx: -12, dy: -8, w: 90, h: 22 };
    let ghost: HTMLElement | null = null, drop: HTMLElement | null = null, aim: Aim = null;
    const onView = self.b !== undefined;   // off the View: the original hidden (the ghost is it); off a bar: left faint where it was
    if (onView && self.i !== undefined) self.at = r0;
    this.dragged = false;
    const move = (m: PointerEvent) => {
      if (!ghost) {
        if (Math.hypot(m.clientX - x0, m.clientY - y0) < 5) return;
        this.dragged = true;
        // a copy of the button (or group), picked up off a pin or out of the Saved macros tab
        ghost = src ? src.cloneNode(true) as HTMLElement : div('macpin', name);
        ghost.removeAttribute('title');
        ghost.classList.remove('onview');
        ghost.classList.add('macpin-ghost');
        ghost.style.left = ghost.style.top = ghost.style.gridArea = '';   // (a View pin's place)
        ghost.style.width = `${grab.w}px`;
        ghost.style.height = `${grab.h}px`;
        if (onView && src) src.style.visibility = 'hidden';
        else if (src?.closest('#macpins-top, .macpinbar')) src.classList.add('lifted');
        drop = div('dock-drop');
        drop.hidden = true;
        document.body.append(ghost, drop);
      }
      ghost.style.transform = `translate(${m.clientX - grab.dx}px, ${m.clientY - grab.dy}px)`;
      aim = this.aim(m.clientX, m.clientY, drop!, ghost, grab, self);
    };
    const end = (ok: boolean) => {
      removeEventListener('pointermove', move, true);
      removeEventListener('pointerup', up, true);
      removeEventListener('pointercancel', cancel, true);
      removeEventListener('keydown', key, true);
      ghost?.remove(); drop?.remove();
      src?.classList.remove('lifted');
      if (onView && src) src.style.visibility = '';
      setTimeout(() => { this.dragged = false; });
      if (!ok) return;
      const p = done(aim);
      if (p) this.set(p);
    };
    const up = () => end(this.dragged);
    const cancel = () => end(false);
    const key = (k: KeyboardEvent) => { if (k.key === 'Escape') { k.stopPropagation(); end(false); } };
    addEventListener('pointermove', move, true);
    addEventListener('pointerup', up, true);
    addEventListener('pointercancel', cancel, true);
    addEventListener('keydown', key, true);
  }

  /** Where a pin let go at x, y (held at grab) goes (null: nowhere), shown on the drop element. */
  private aim(x: number, y: number, drop: HTMLElement, ghost: HTMLElement, grab: Grab, self: Self): Aim {
    ghost.style.visibility = 'hidden';   // (it is pointer-events: none already; this is for elementFromPoint's sake in every browser)
    const t = document.elementFromPoint(x, y) as HTMLElement | null;
    ghost.style.visibility = '';
    drop.hidden = true;
    drop.classList.remove('caret', 'h');
    const show = (r: { left: number; top: number; width: number; height: number }, caret = false) => {
      Object.assign(drop.style, { left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px` });
      drop.classList.toggle('caret', caret);
      drop.hidden = false;
    };
    if (!t) return null;
    // a side of a button on the View: joins its block, in its row (left, right) or its column (top, bottom)
    const vp = t.closest<HTMLElement>('.macblock.onview > .macpin');
    const vb = vp && +(vp.parentElement as HTMLElement).dataset.b!;
    // a View button let go where it was (it is hidden, so found by where it was)
    const a = self.at;
    if (a && x >= a.left && x <= a.right && y >= a.top && y <= a.bottom) return 'stay';
    if (vp && !(vb === self.b && self.i === undefined)) {
      const r = vp.getBoundingClientRect(), fy = (y - r.top) / r.height;
      const side: Side = fy < 0.3 ? 'top' : fy > 0.7 ? 'bottom' : x > r.left + r.width / 2 ? 'right' : 'left';
      if (side === 'left' || side === 'right') show({ left: (side === 'right' ? r.right : r.left) - 1, top: r.top, width: 3, height: r.height }, true);
      else {
        show({ left: r.left, top: (side === 'bottom' ? r.bottom : r.top) - 1, width: r.width, height: 3 }, true);
        drop.classList.add('h');
      }
      return { zone: { k: 'join', b: vb!, r: +vp.dataset.r!, c: +vp.dataset.c!, side } };
    }
    // before a pin in a bar (the header's or a panel's)
    const pin = !self.viewOnly && t.closest<HTMLElement>('#macpins-top > .macpin, .macpinbar > .macpin');
    if (pin) {
      const r = pin.getBoundingClientRect(), after = x > r.left + r.width / 2, i = +pin.dataset.i! + (after ? 1 : 0);
      show({ left: (after ? r.right : r.left) - 1, top: r.top, width: 3, height: r.height }, true);
      const pane = pin.closest<HTMLElement>('.pane[data-tab]');
      return { zone: pane ? { k: 'pane', tab: pane.dataset.tab! } : { k: 'top' }, before: i };
    }
    // the header lets the pointer through but for its controls, so it too is found by where it is
    const hr = this.h.top.getBoundingClientRect();
    if (t.closest('#top') || (!t.closest('.pane, .fwin') && x >= hr.left && x <= hr.right && y >= hr.top && y <= hr.bottom)) {
      if (self.viewOnly) return null;
      const r = this.topBar.getBoundingClientRect();
      show(r.width ? { left: r.left, top: r.top, width: r.width + 4, height: r.height } : { left: r.left - 2, top: hr.top + 4, width: 4, height: hr.height - 8 }, !r.width);
      return { zone: { k: 'top' } };
    }
    const pane = t.closest<HTMLElement>('.pane[data-tab]');
    const tab = pane?.dataset.tab;
    // the View: it lets the pointer through to the canvas under the layout, so it is found by where it is
    if (tab === 'viewport' || (!pane && (t.id === 'c' || t.closest('.dock-group[data-active="viewport"]')))) {
      const r = this.h.view.getBoundingClientRect();
      if (!r.width || !r.height || x < r.left || x > r.right || y < r.top || y > r.bottom) return null;
      // the button's (block's) top-left where it is held, kept wholly in the View (as placeView keeps it)
      const left = Math.max(r.left, Math.min(x - grab.dx, r.right - grab.w)), top = Math.max(r.top, Math.min(y - grab.dy, r.bottom - grab.h));
      show({ left, top, width: grab.w, height: grab.h });
      return { zone: { k: 'view', x: (left - r.left) / r.width, y: (top - r.top) / r.height } };
    }
    if (self.viewOnly || !pane || !tab || NO_BAR.has(tab)) return null;
    const r = pane.getBoundingClientRect(), h = Math.min(32, r.height / 3);
    show({ left: r.left + 2, top: r.bottom - h - 2, width: r.width - 4, height: h });
    return { zone: { k: 'pane', tab } };
  }
}
